import React, { useContext } from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { AuthContext } from 'core/components/auth';
import { FormField } from 'core/components/form';
import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { withToast } from 'core/components/toast';
import { useStore } from 'core/store/store.mobx';

import TroubleshootingToggle from './troubleshooting-toggle';
import useUpdatingPriceMessage from '../hooks/use-updating-price-message';
import { isQuoteStepRailEnabled } from 'core/helpers/quote-revisit';
import useStyles from '../footer.styles';

// Subtitle under the "Update Offer" title, shown only when the agent has
// unsaved edits, so they know pressing Update will apply those edits
// (#30230 follow-up).
//
// We intentionally say nothing in the purely stale case (offer's rate control
// date is 1-29 days old). Earlier copy explained the stale re-rate to agents,
// but agency agents read "pricing is out of date" / "recalculate" as Branch
// changing their price and coverage behind their back, which generated
// consistent negative feedback (#31893). The plain "Update Offer" title is
// enough there. The subtitle exists only to surface the one thing the title
// doesn't already imply: that the agent's own edits will be applied. When an
// offer is both stale and dirty we still show only the edits copy.
//
// #35629: continuing to checkout on a mutual-brand offer rated on an earlier day normally refreshes
// its payment schedule silently; this prompt only appears when that refresh failed, so the agent
// needs to know why they are being asked to update. The copy names only the payment schedule so it
// doesn't read as a re-price (the #31893 concern); within the brand's rate control date window the
// re-rate keeps the rate control date.
const UpdateReasonCopy = {
  dirty: 'Updating will apply the changes you made to this offer.',
  both: 'Updating will apply the changes you made to this offer.',
  paymentSchedule:
    'We could not refresh the payment schedule automatically. Updating refreshes it so payments start from today.'
};

const UpdateOffer = observer(({ onUpdate, updateReason }) => {
  const { offer: store } = useStore();
  const classes = useStyles();

  const session = useContext(AuthContext);
  const showOverrideCheckbox = !session.isAgency && session.username !== store.offer?.quote?.rep;

  const subtitle = updateReason ? UpdateReasonCopy[updateReason] : null;
  const loadingMessage = useUpdatingPriceMessage(store.loading);

  return (
    <>
      <div className={classes.titleContainer}>
        <Label className={classes.title} type="subtitle">
          {isQuoteStepRailEnabled() ? 'Update price' : 'Update Offer'}
        </Label>
        {subtitle && (
          <Label className={classes.subtitle} type="subtitle">
            {subtitle}
          </Label>
        )}
      </div>
      <div className={classes.updateActions}>
        {showOverrideCheckbox && (
          <FormField
            mode="light"
            name="dontOverwriteRep"
            label="Don't overwrite rep"
            type="checkbox"
            className={classes.checkbox}
            disabled={store.loading}
          />
        )}
        <TroubleshootingToggle />
        <Button
          onClick={onUpdate}
          disabled={store.loading}
          loading={store.loading}
          loadingMessage={loadingMessage}
          variant="contained"
          color="primary"
          className={classNames(classes.btn, { [classes.btnLoadingMessage]: store.loading })}
        >
          <Label className={classes.btnLabel}>{isQuoteStepRailEnabled() ? 'Update price' : 'Update Offer Price'}</Label>
        </Button>
      </div>
    </>
  );
});
export default withToast(UpdateOffer);
